import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import ERPLayout from "@/components/feature/ERPLayout";
import BackToTop from "@/components/base/BackToTop";
import { jobCardsApi } from "@/services/api";
import { exportToCSV, exportToExcel, exportToPDF } from "@/utils/export";
import BulkActionBar from "./components/BulkActionBar";

const statusColors: Record<string, { bg: string; text: string }> = {
  "Pending":     { bg: "#fffbeb", text: "#d97706" },
  "In Progress": { bg: "#fef3c7", text: "#b45309" },
  "Completed":   { bg: "#f0fdf4", text: "#10b981" },
  "Delivered":   { bg: "#ecfdf5", text: "#059669" },
};

const priorityColors: Record<string, string> = {
  "Critical": "#ef4444",
  "High":     "#d97706",
  "Medium":   "#f59e0b",
  "Low":      "#10b981",
};

interface JobCard {
  id: number;
  jobNo: string;
  title: string;
  client: string;
  location: string;
  status: string;
  priority: string;
  progress: number;
  dueDate: string;
  services: string[];
  fieldTeam: string[];
}

// ── Normalize DB row → frontend shape ────────────────────────────────────────
// The backend returns snake_case DB columns; the page (and mock data) uses camelCase.
// This mapper bridges the gap so real API data renders correctly.
function normalizeJobCard(row: Record<string, unknown>): JobCard {
  // If it already looks like a mock/normalized record, pass through
  if (typeof row.jobNo === "string") return row as unknown as JobCard;

  const services: string[] = [];
  if (row.services && Array.isArray(row.services)) {
    for (const s of row.services as Record<string, unknown>[]) {
      if (s.service_name) services.push(String(s.service_name));
    }
  }

  const fieldTeam: string[] = [];
  if (row.team && Array.isArray(row.team)) {
    for (const t of row.team as Record<string, unknown>[]) {
      if (t.team_type === "field" && t.employee_name) fieldTeam.push(String(t.employee_name));
    }
  }

  // Map priority from DB value (lowercase) to display value (Title Case)
  const priorityMap: Record<string, string> = {
    critical: "Critical", high: "High", medium: "Medium", low: "Low",
  };
  const rawPriority = String(row.priority || "medium").toLowerCase();
  const priority = priorityMap[rawPriority] || "Medium";

  // Map status from DB value to display value
  const statusMap: Record<string, string> = {
    pending: "Pending",
    in_progress: "In Progress",
    completed: "Completed",
    delivered: "Delivered",
  };
  const rawStatus = String(row.status || "pending").toLowerCase().replace(/ /g, "_");
  const status = statusMap[rawStatus] || String(row.status || "Pending");

  return {
    id: Number(row.id),
    jobNo: String(row.job_no || ""),
    title: String(row.title || ""),
    client: String(row.client_name || row.client || ""),
    location: String(row.location || ""),
    status,
    priority,
    progress: Number(row.progress || 0),
    dueDate: row.due_date ? String(row.due_date).split("T")[0] : "",
    services,
    fieldTeam,
  } as unknown as JobCard;
}

const LIMIT = 12;

// ── Pagination ──────────────────────────────────────────────────────────────
interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  showing: number;
  onPage: (p: number) => void;
}

function Pagination({ page, totalPages, total, showing, onPage }: PaginationProps) {
  if (totalPages <= 1) return (
    <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
      <p className="text-xs text-gray-500">Showing <strong>{showing}</strong> of <strong>{total}</strong> job cards</p>
    </div>
  );

  const pages: (number | "...")[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push("...");
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push("...");
    pages.push(totalPages);
  }

  return (
    <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
      <p className="text-xs text-gray-500">
        Showing <strong>{showing}</strong> of <strong>{total}</strong> job cards
        <span className="ml-2 text-gray-400">· Page {page} of {totalPages}</span>
      </p>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(page - 1)} disabled={page === 1}
          className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-all">
          <i className="ri-arrow-left-s-line text-sm" />
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="w-7 h-7 flex items-center justify-center text-xs text-gray-400">…</span>
          ) : (
            <button key={p} onClick={() => onPage(p as number)}
              className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-semibold cursor-pointer transition-all ${
                page === p ? "bg-amber-500 text-white" : "border border-gray-200 text-gray-500 hover:bg-amber-50"
              }`}>{p}</button>
          )
        )}
        <button onClick={() => onPage(page + 1)} disabled={page === totalPages}
          className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-all">
          <i className="ri-arrow-right-s-line text-sm" />
        </button>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function JobCardsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filter from URL on mount
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "All");
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get("priority") || "All");
  const [dueDateFilter, setDueDateFilter] = useState(searchParams.get("filter") || "All");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [jobCards, setJobCards] = useState<JobCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const [usingMock, setUsingMock] = useState(false);
  const [apiError, setApiError] = useState("");

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  // Close export menu on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // ── Export (all / selected) ──────────────────────────────────────────────
  const getExportData = (source: JobCard[]) =>
    source.map((j) => ({
      "Job No": j.jobNo,
      "Title": j.title,
      "Client": j.client,
      "Location": j.location,
      "Status": j.status,
      "Priority": j.priority,
      "Progress": `${j.progress}%`,
      "Due Date": j.dueDate,
      "Services": j.services.join(", "),
      "Field Team": j.fieldTeam.join(", "),
    }));

  const handleExportCSV = () => {
    exportToCSV(getExportData(jobCards), `job-cards-${new Date().toISOString().split("T")[0]}`);
    setExportMenuOpen(false);
  };
  const handleExportExcel = async () => {
    await exportToExcel(getExportData(jobCards), `job-cards-${new Date().toISOString().split("T")[0]}`, "Job Cards");
    setExportMenuOpen(false);
  };
  const handleExportPDF = () => {
    exportToPDF("job-cards-table", `job-cards-${new Date().toISOString().split("T")[0]}`, "Job Cards Report");
    setExportMenuOpen(false);
  };

  // ── Fetch ────────────────────────────────────────────────────────────────
  const applyLocalFilters = (list: JobCard[]) => {
    let out = list;
    if (statusFilter !== "All") out = out.filter((j) => j.status === statusFilter);
    if (priorityFilter !== "All") out = out.filter((j) => j.priority === priorityFilter);
    if (dueDateFilter !== "All") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      out = out.filter((j) => {
        const due = new Date(j.dueDate);
        if (dueDateFilter === "Overdue") return due < today && j.status !== "Delivered";
        if (dueDateFilter === "Today") return due.toDateString() === today.toDateString();
        if (dueDateFilter === "This Week") {
          const weekEnd = new Date(today); weekEnd.setDate(today.getDate() + 7);
          return due >= today && due <= weekEnd;
        }
        if (dueDateFilter === "This Month") {
          return due.getMonth() === today.getMonth() && due.getFullYear() === today.getFullYear();
        }
        return true;
      });
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter((j) => j.jobNo.toLowerCase().includes(q) || j.title.toLowerCase().includes(q) || j.client.toLowerCase().includes(q));
    }
    return out;
  };

  const fetchJobCards = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params: Record<string, string | number> = { page, limit: LIMIT };
      if (statusFilter !== "All") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const data = await jobCardsApi.getAll(params as Parameters<typeof jobCardsApi.getAll>[0]);
      const rows: Record<string, unknown>[] = Array.isArray(data) ? data : data.data ?? [];
      const normalized = rows.map(normalizeJobCard);
      setJobCards(normalized);
      setTotal(data.pagination?.total ?? normalized.length);
      setUsingMock(false);
      setApiError("");
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : String(err);
      setApiError(errMsg);
      setUsingMock(true);
      // Keep existing data if we have it, don't clear on error
      if (jobCards.length === 0) {
        setJobCards([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter, dueDateFilter, search, page]);

  useEffect(() => {
    const timer = setTimeout(fetchJobCards, 300);
    return () => clearTimeout(timer);
  }, [fetchJobCards]);

  useEffect(() => { setPage(1); }, [statusFilter, priorityFilter, dueDateFilter, search]);

  // Clear selection when page/filter changes
  useEffect(() => { setSelectedIds(new Set()); }, [page, statusFilter, search]);

  // Sync URL params with filter state
  useEffect(() => {
    const params: Record<string, string> = {};
    if (statusFilter !== "All") params.status = statusFilter;
    if (priorityFilter !== "All") params.priority = priorityFilter;
    if (dueDateFilter !== "All") params.filter = dueDateFilter;
    if (search.trim()) params.search = search.trim();
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter, dueDateFilter, search, page]);

  const totalPages = Math.ceil(total / LIMIT);
  const filtered = jobCards;

  // ── Selection helpers ────────────────────────────────────────────────────
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((j) => j.id)));
    }
  };

  const selectAll = () => setSelectedIds(new Set(filtered.map((j) => j.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const allSelected = filtered.length > 0 && selectedIds.size === filtered.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < filtered.length;

  // ── Bulk actions ─────────────────────────────────────────────────────────
  const handleBulkStatusChange = (status: string) => {
    setJobCards((prev) =>
      prev.map((j) => (selectedIds.has(j.id) ? { ...j, status } : j))
    );
    showToast(`Updated ${selectedIds.size} job card${selectedIds.size > 1 ? "s" : ""} to "${status}"`);
    clearSelection();
  };

  const handleBulkAssign = (employee: string) => {
    setJobCards((prev) =>
      prev.map((j) =>
        selectedIds.has(j.id)
          ? { ...j, fieldTeam: j.fieldTeam.includes(employee) ? j.fieldTeam : [...j.fieldTeam, employee] }
          : j
      )
    );
    showToast(`Assigned ${employee} to ${selectedIds.size} job card${selectedIds.size > 1 ? "s" : ""}`);
    clearSelection();
  };

  const handleBulkExport = async (format: "csv" | "excel" | "pdf") => {
    const selected = filtered.filter((j) => selectedIds.has(j.id));
    const data = getExportData(selected);
    const filename = `job-cards-selected-${new Date().toISOString().split("T")[0]}`;
    if (format === "csv") exportToCSV(data, filename);
    else if (format === "excel") await exportToExcel(data, filename, "Job Cards");
    else exportToPDF("job-cards-table", filename, "Job Cards Report");
    showToast(`Exported ${selected.length} job card${selected.length > 1 ? "s" : ""} as ${format.toUpperCase()}`);
  };

  return (
    <ERPLayout title="Job Card Management">
      <div className="p-6">
        {/* Toast */}
        {toast && (
          <div className="fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm font-medium text-amber-700 transition-all" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-checkbox-circle-line text-lg text-amber-500" /></span>
            {toast}
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Job Card Management</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {loading ? "Loading..." : `${total} total job cards`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative" ref={exportMenuRef}>
              <button
                onClick={() => setExportMenuOpen(!exportMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-download-2-line text-sm" /></span>
                Export
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${exportMenuOpen ? "rotate-180" : ""}`} />
              </button>
              {exportMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-xl z-20 overflow-hidden" style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}>
                  <button onClick={handleExportCSV} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors text-left">
                    <i className="ri-file-list-3-line text-gray-400" />CSV
                  </button>
                  <button onClick={handleExportExcel} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors text-left">
                    <i className="ri-file-excel-line text-amber-500" />Excel
                  </button>
                  <button onClick={handleExportPDF} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors text-left">
                    <i className="ri-file-pdf-line text-red-500" />PDF
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={() => navigate("/job-cards/new")}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></span>
              New Job Card
            </button>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4 space-y-3">
          {/* Row 1: Search + View toggle */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-gray-400 text-sm" />
              </span>
              <input
                type="text"
                placeholder="Search job no, title, client..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
              />
            </div>
            <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1">
              <button
                onClick={() => setView("grid")}
                className={`w-7 h-7 flex items-center justify-center rounded cursor-pointer ${view === "grid" ? "bg-amber-500 text-white" : "text-gray-400 hover:text-gray-600"}`}
              >
                <i className="ri-grid-line text-sm" />
              </button>
              <button
                onClick={() => setView("list")}
                className={`w-7 h-7 flex items-center justify-center rounded cursor-pointer ${view === "list" ? "bg-amber-500 text-white" : "text-gray-400 hover:text-gray-600"}`}
              >
                <i className="ri-list-check text-sm" />
              </button>
            </div>
          </div>

          {/* Row 2: Status tabs + Priority + Due Date + Clear */}
          <div className="flex flex-wrap gap-2 items-center">
            {/* Status tabs */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {["All", "Pending", "In Progress", "Completed", "Delivered"].map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    statusFilter === s ? "bg-white text-amber-700 font-semibold" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            {/* Priority dropdown */}
            <div className="relative">
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className={`pl-3 pr-7 py-1.5 border rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-amber-500/30 cursor-pointer appearance-none transition-all ${
                  priorityFilter !== "All" ? "border-amber-300 bg-amber-50 text-amber-700" : "border-gray-200 text-gray-600"
                }`}
              >
                <option value="All">All Priorities</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
              <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <i className="ri-arrow-down-s-line text-gray-400 text-xs" />
              </span>
            </div>

            {/* Due Date dropdown */}
            <div className="relative">
              <select
                value={dueDateFilter}
                onChange={(e) => setDueDateFilter(e.target.value)}
                className={`pl-3 pr-7 py-1.5 border rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-amber-500/30 cursor-pointer appearance-none transition-all ${
                  dueDateFilter !== "All" ? "border-rose-300 bg-rose-50 text-rose-700" : "border-gray-200 text-gray-600"
                }`}
              >
                <option value="All">All Due Dates</option>
                <option value="Overdue">Overdue</option>
                <option value="Today">Due Today</option>
                <option value="This Week">Due This Week</option>
                <option value="This Month">Due This Month</option>
              </select>
              <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <i className="ri-arrow-down-s-line text-gray-400 text-xs" />
              </span>
            </div>

            {/* Active filter chips + clear */}
            {(priorityFilter !== "All" || dueDateFilter !== "All" || statusFilter !== "All" || search) && (
              <button
                onClick={() => { setPriorityFilter("All"); setDueDateFilter("All"); setStatusFilter("All"); setSearch(""); }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 text-gray-500 rounded-lg text-xs font-medium hover:bg-gray-200 cursor-pointer whitespace-nowrap transition-colors"
              >
                <span className="w-3 h-3 flex items-center justify-center"><i className="ri-close-line text-xs" /></span>
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selectedIds.size > 0 && (
          <div className="mb-4">
            <BulkActionBar
              selectedCount={selectedIds.size}
              totalCount={filtered.length}
              onClearSelection={clearSelection}
              onSelectAll={selectAll}
              onBulkStatusChange={handleBulkStatusChange}
              onBulkAssign={handleBulkAssign}
              onBulkExport={handleBulkExport}
            />
          </div>
        )}

        {/* API Error Banner */}
        {usingMock && (
          <div className="mb-4 p-3 rounded-xl border border-amber-200 bg-amber-50 flex items-start gap-3">
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-error-warning-fill text-amber-500" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-amber-700">Unable to load job cards from server</p>
              <p className="text-xs text-amber-600 mt-0.5 break-all">Error: {apiError}</p>
              <p className="text-xs text-amber-600 mt-1">
                {jobCards.length > 0 
                  ? "Showing previously loaded data. Changes may not be saved to the database."
                  : "No job cards loaded. Please check your connection and retry."}{" "}
                <Link to="/admin/database" className="underline font-semibold">Run API Diagnostics</Link>
              </p>
            </div>
            <button 
              onClick={() => { setUsingMock(false); setApiError(""); fetchJobCards(); }} 
              className="flex-shrink-0 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 rounded-lg text-xs text-amber-700 font-semibold cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-refresh-line mr-1" />Retry
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
            <i className="ri-error-warning-line" />{error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <i className="ri-loader-4-line text-3xl text-amber-500 animate-spin" />
              <p className="text-sm text-gray-500">Loading job cards...</p>
            </div>
          </div>
        )}

        {/* ── Grid View ── */}
        {!loading && view === "grid" && (
          <>
            {/* Select All row */}
            {filtered.length > 0 && (
              <div className="flex items-center gap-3 mb-3 px-1">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <div
                    onClick={toggleSelectAll}
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center cursor-pointer transition-all flex-shrink-0 ${
                      allSelected ? "bg-amber-500 border-amber-500" : someSelected ? "bg-amber-100 border-amber-400" : "border-gray-300 hover:border-amber-400"
                    }`}
                  >
                    {allSelected && <i className="ri-check-line text-white text-xs" />}
                    {someSelected && !allSelected && <div className="w-2 h-0.5 bg-amber-500 rounded" />}
                  </div>
                  <span className="text-xs text-gray-500 font-medium">
                    {allSelected ? "Deselect all" : `Select all ${filtered.length}`}
                  </span>
                </label>
                {selectedIds.size > 0 && (
                  <span className="text-xs text-amber-600 font-semibold">{selectedIds.size} selected</span>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map((job) => {
                const sc = statusColors[job.status] || { bg: "#f3f4f6", text: "#6b7280" };
                const isSelected = selectedIds.has(job.id);
                return (
                  <div
                    key={job.id}
                    className={`bg-white rounded-xl border transition-all overflow-hidden group relative ${
                      isSelected ? "border-amber-400 ring-2 ring-amber-200" : "border-gray-100 hover:border-amber-200"
                    }`}
                  >
                    {/* Checkbox overlay */}
                    <div
                      className="absolute top-3 left-3 z-10"
                      onClick={(e) => { e.stopPropagation(); toggleSelect(job.id); }}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
                        isSelected ? "bg-amber-500 border-amber-500" : "border-gray-300 bg-white hover:border-amber-400 opacity-0 group-hover:opacity-100"
                      }`}>
                        {isSelected && <i className="ri-check-line text-white text-xs" />}
                      </div>
                    </div>

                    <div
                      className="px-4 py-3 border-b border-gray-50 flex items-center justify-between cursor-pointer"
                      style={{ background: isSelected ? "#fef3c7" : sc.bg }}
                      onClick={() => navigate(`/job-cards/${job.id}`)}
                    >
                      <span className="text-sm font-bold pl-6" style={{ color: sc.text }}>{job.jobNo}</span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white" style={{ color: sc.text }}>{job.status}</span>
                    </div>

                    <div className="p-4 cursor-pointer" onClick={() => navigate(`/job-cards/${job.id}`)}>
                      <h4 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2">{job.title}</h4>
                      <div className="flex items-center gap-1.5 mb-2">
                        <span className="w-3 h-3 flex items-center justify-center"><i className="ri-building-line text-gray-400 text-xs" /></span>
                        <span className="text-xs text-gray-500">{job.client}</span>
                      </div>
                      <div className="flex items-center gap-1.5 mb-3">
                        <span className="w-3 h-3 flex items-center justify-center"><i className="ri-map-pin-line text-gray-400 text-xs" /></span>
                        <span className="text-xs text-gray-500">{job.location}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {job.services.slice(0, 2).map((s) => (
                          <span key={s} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">{s}</span>
                        ))}
                        {job.services.length > 2 && (
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">+{job.services.length - 2}</span>
                        )}
                      </div>
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-500">Progress</span>
                          <span className="text-xs font-semibold text-gray-700">{job.progress}%</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${job.progress}%` }} />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-calendar-line text-gray-400 text-xs" /></span>
                          <span className="text-xs text-gray-500">Due: {job.dueDate}</span>
                        </div>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: priorityColors[job.priority], background: `${priorityColors[job.priority]}15` }}>
                          {job.priority}
                        </span>
                      </div>
                    </div>

                    <div className="px-4 py-2.5 border-t border-gray-50 flex items-center justify-between bg-gray-50/50">
                      <div className="flex -space-x-1.5">
                        {job.fieldTeam.slice(0, 3).map((m, i) => (
                          <div key={i} className="w-6 h-6 rounded-full bg-amber-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                            {m[0]}
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-1">
                        <Link to={`/job-cards/${job.id}`} onClick={(e) => e.stopPropagation()} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 cursor-pointer">
                          <i className="ri-eye-line text-sm" />
                        </Link>
                        <Link to={`/job-cards/${job.id}`} onClick={(e) => e.stopPropagation()} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 cursor-pointer">
                          <i className="ri-edit-line text-sm" />
                        </Link>
                        <Link to={`/job-cards/${job.id}/print`} onClick={(e) => e.stopPropagation()} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer">
                          <i className="ri-printer-line text-sm" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 bg-white rounded-xl border border-gray-100 overflow-hidden">
              <Pagination page={page} totalPages={totalPages} total={total} showing={filtered.length} onPage={setPage} />
            </div>
          </>
        )}

        {/* ── List View ── */}
        {!loading && view === "list" && (
          <div id="job-cards-table" className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    {/* Select all checkbox */}
                    <th className="px-4 py-3 w-10">
                      <div
                        onClick={toggleSelectAll}
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center cursor-pointer transition-all mx-auto ${
                          allSelected ? "bg-amber-500 border-amber-500" : someSelected ? "bg-amber-100 border-amber-400" : "border-gray-300 hover:border-amber-400"
                        }`}
                      >
                        {allSelected && <i className="ri-check-line text-white text-xs" />}
                        {someSelected && !allSelected && <div className="w-2 h-0.5 bg-amber-500 rounded" />}
                      </div>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Job No.</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Title / Client</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Services</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Progress</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Due Date</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((job) => {
                    const sc = statusColors[job.status] || { bg: "#f3f4f6", text: "#6b7280" };
                    const isSelected = selectedIds.has(job.id);
                    return (
                      <tr
                        key={job.id}
                        className={`border-b border-gray-50 transition-colors ${isSelected ? "bg-amber-50/60" : "hover:bg-gray-50"}`}
                      >
                        <td className="px-4 py-3 w-10">
                          <div
                            onClick={() => toggleSelect(job.id)}
                            className={`w-4 h-4 rounded border-2 flex items-center justify-center cursor-pointer transition-all mx-auto ${
                              isSelected ? "bg-amber-500 border-amber-500" : "border-gray-300 hover:border-amber-400"
                            }`}
                          >
                            {isSelected && <i className="ri-check-line text-white text-xs" />}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-bold text-gray-800">{job.jobNo}</span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-gray-900 truncate max-w-xs">{job.title}</p>
                          <p className="text-xs text-gray-500">{job.client}</p>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {job.services.slice(0, 2).map((s) => (
                              <span key={s} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">{s}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: sc.bg, color: sc.text }}>{job.status}</span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-amber-500 rounded-full" style={{ width: `${job.progress}%` }} />
                            </div>
                            <span className="text-xs text-gray-500 w-8">{job.progress}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center hidden lg:table-cell">
                          <span className="text-xs text-gray-600">{job.dueDate}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <Link to={`/job-cards/${job.id}`} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 cursor-pointer"><i className="ri-eye-line text-sm" /></Link>
                            <Link to={`/job-cards/${job.id}`} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 cursor-pointer"><i className="ri-edit-line text-sm" /></Link>
                            <Link to={`/job-cards/${job.id}/print`} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer"><i className="ri-printer-line text-sm" /></Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} showing={filtered.length} onPage={setPage} />
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-100 py-16 text-center">
            <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-file-list-3-line text-4xl text-gray-300" /></span>
            <p className="text-gray-500 text-sm">No job cards found</p>
          </div>
        )}
      </div>
      <BackToTop scrollContainerId="erp-main-scroll" />
    </ERPLayout>
  );
}
